import {
  isMapLegendLineSymbol,
  normalizeMapLegendSymbol,
  SYMBOL_CIRCLE,
  SYMBOL_DASHED,
  SYMBOL_DIAMOND,
  SYMBOL_DOTTED,
  SYMBOL_HEXAGON,
  SYMBOL_LINE,
  SYMBOL_LONG_DASHED,
  SYMBOL_SQUARE,
  SYMBOL_STAR,
  SYMBOL_TRIANGLE,
  type MapLegendSymbol,
} from '@buxus/shared'

const ICON_SIZE = 16

type Props = {
  symbol: string
  color: string
  size?: number
}

function lineDasharray(symbol: MapLegendSymbol): string | undefined {
  switch (symbol) {
    case SYMBOL_DOTTED:
      return '1 6'
    case SYMBOL_DASHED:
      return '3 3'
    case SYMBOL_LONG_DASHED:
      return '6 4'
    default:
      return undefined
  }
}

function LegendLineIcon({ symbol, color, size }: { symbol: MapLegendSymbol; color: string; size: number }) {
  const dash = lineDasharray(symbol)
  return (
    <svg
      className="operator-legend-symbol operator-legend-symbol--line"
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden
    >
      <line
        x1="0"
        y1={size / 2}
        x2={size}
        y2={size / 2}
        stroke={color}
        strokeWidth="2.5"
        strokeDasharray={dash}
        strokeLinecap="round"
      />
    </svg>
  )
}

function LegendShapeIcon({ symbol, color, size }: { symbol: MapLegendSymbol; color: string; size: number }) {
  const common = { fill: color, stroke: 'none' as const }
  switch (symbol) {
    case SYMBOL_CIRCLE:
      return (
        <svg className="operator-legend-symbol" width={size} height={size} viewBox="0 0 16 16" aria-hidden>
          <circle cx="8" cy="8" r="6.5" {...common} />
        </svg>
      )
    case SYMBOL_SQUARE:
      return (
        <svg className="operator-legend-symbol" width={size} height={size} viewBox="0 0 16 16" aria-hidden>
          <rect x="2.5" y="2.5" width="11" height="11" {...common} />
        </svg>
      )
    case SYMBOL_TRIANGLE:
      return (
        <svg className="operator-legend-symbol" width={size} height={size} viewBox="0 0 16 16" aria-hidden>
          <polygon points="8,2 14,14 2,14" {...common} />
        </svg>
      )
    case SYMBOL_DIAMOND:
      return (
        <svg className="operator-legend-symbol" width={size} height={size} viewBox="0 0 16 16" aria-hidden>
          <polygon points="8,1.5 14.5,8 8,14.5 1.5,8" {...common} />
        </svg>
      )
    case SYMBOL_HEXAGON:
      return (
        <svg className="operator-legend-symbol" width={size} height={size} viewBox="0 0 16 16" aria-hidden>
          <polygon points="8,1.5 13.5,4.75 13.5,11.25 8,14.5 2.5,11.25 2.5,4.75" {...common} />
        </svg>
      )
    case SYMBOL_STAR:
      return (
        <svg className="operator-legend-symbol" width={size} height={size} viewBox="0 0 16 16" aria-hidden>
          <polygon
            points="8,1.5 9.85,6.1 14.75,6.1 10.7,9.1 12.35,14 8,11.2 3.65,14 5.3,9.1 1.25,6.1 6.15,6.1"
            {...common}
          />
        </svg>
      )
    default:
      return <LegendLineIcon symbol={SYMBOL_LINE} color={color} size={size} />
  }
}

/** Mobile `NormalizedIcon` parity for operator map legend swatches. */
export function OperatorLegendSymbol({ symbol, color, size = ICON_SIZE }: Props) {
  const normalized = normalizeMapLegendSymbol(symbol)
  if (isMapLegendLineSymbol(normalized)) {
    return <LegendLineIcon symbol={normalized} color={color} size={size} />
  }
  return <LegendShapeIcon symbol={normalized} color={color} size={size} />
}
